<script lang="ts" setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useToast } from 'vue-toastification';
import { Quiz } from '@/models/Quiz';
import { QuizAnswer } from '@/models/QuizAnswer';
import { QuizQuestion } from '@/models/QuizQuestion';
import ConfettiExplosion from 'vue-confetti-explosion';

const router = useRouter();
const route = useRoute();

const props = defineProps<{
  quizzes: Array<Quiz>,
}>();

const hasConfetti = ref(false);
const confetti = ref({
  stageHeight: window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight,
  stageWidth: (window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth) * 1.33,
});

const makeConfetti = async () => {
  hasConfetti.value = false;
  await nextTick();
  hasConfetti.value = true;
};

const isCorrect = (answer: QuizAnswer) => {
  return answer.correct ? answer.selected : !answer.selected;
};

const isSingleChoice = (question: QuizQuestion) => {
  return question.answers?.filter(answer => answer.correct).length === 1;
};

const code = (question: QuizQuestion) => {
  let code = '';

  question.code.lines.forEach(line => {
    code += `<span class="dive-code">${ line }</span><br />`;
  })

  return code;
};

const formatted = (questionText: string) => {
  return questionText.replace('___', '<span class="gap"></span>');
};

const quiz = computed(() => {
  return props.quizzes.find((quiz: Quiz) => quiz.id === route.params.id);
});

onMounted(() => {
  makeConfetti();
  if (quiz.value && quiz.value.score >= 50) {
    const toast = useToast();

    toast.success('Gut gemacht, weiter so! 🔥', {
      id: 'well-done',
    });
  }
});
</script>


<template>
  <section id="quiz-result" tabindex="-1" aria-label="Quiz-Auswertung">
    <ConfettiExplosion
      v-if="hasConfetti"
      :force="0.7"
      :particleCount="200"
      :particleSize="14"
      :duration="4500"
      :stageHeight="confetti.stageHeight"
      :stageWidth="confetti.stageWidth"
    />
    <div class="result" v-if="quiz">
      <div class="row">
        <div class="column">
          <div class="emoji">
            <span v-if="quiz.score >= 85">🤩</span>
            <span v-else-if="quiz.score >= 75">😍</span>
            <span v-else-if="quiz.score >= 50">😊</span>
            <span v-else>🥳</span>
          </div>
          <span class="percent">
            <vue3-autocounter
                ref='counter'
                :startAmount='0'
                :endAmount='quiz.score || 0'
                :duration='1'
                prefix='Dein Ergebnis: '
                suffix="%"
                :decimals='0'
                :autoinit='true'
            />
          </span>
          <div
            class="question"
            v-for="(question, questionIndex) in quiz.questions"
            :key="questionIndex"
          >
            <div class="question__title">
              <p class="question__type">
                <span v-if="isSingleChoice(question)">Single Choice</span>
                <span v-else>Multiple Choice</span>
              </p>
              <h3 v-html="formatted(question.text)"></h3>
            </div>
            <div class="question__code" v-if="question.code">
              <pre v-highlightjs :class="question.code.language">
                <code class="javascript" v-html="code(question)"></code>
              </pre>
            </div>
            <div class="question__image" v-if="question.image">
              <img :src="require(`@/assets/quiz/images/${quiz.id}/${question.image}`)" alt="" />
            </div>
            <div
              class="question__answer result"
              :class="{
                selected: answer.selected,
                correctAnswer: isCorrect(answer),
                wrongAnswer: !isCorrect(answer),
                singleChoice: isSingleChoice(question),
              }"
              :aria-selected="answer.selected"
              v-for="(answer, answerIndex) in quiz.questions[questionIndex].answers"
              v-bind:key="answerIndex"
            >
              {{ answer.text }}
            </div>
            <div class="explanation" v-if="quiz.questions[questionIndex].explanation">
              <p><strong>Erklärung:</strong> {{ quiz.questions[questionIndex].explanation }}</p>
            </div>
            <div class="meta" v-if="quiz.questions">
              <small>
                <strong>{{ questionIndex + 1 }}</strong> von <strong>{{ quiz.questions.length }}</strong>
              </small>
            </div>
          </div>

          <button class="back" v-on:click="router.push({ name: 'home' })">
            Zurück zur Startseite
          </button>
        </div>
      </div>
    </div>
  </section>
</template>


<style lang="scss" scoped>
@import '@/assets/scss/quiz.scss';

.result {
  text-align: center;

  .row .column {
    padding: 2em;
  }

  .question {
    margin-bottom: 3em;

    &__answer {
      text-align: left;
    }
  }

  .emoji {
    font-size: $font-size-xxl;
    margin-bottom: 0.5em;
  }

  .wrongAnswer {
    background: $color-error;
    color: $color-light;
  }

  .selected.correctAnswer {
    background: $color-primary-400;
  }

  .percent {
    display: block;
    font-size: $font-size-xl;
    font-weight: $font-weight-bold;
    margin-bottom: 1.5em;
    text-decoration: underline;
    text-decoration-color: $color-primary-400;
  }

  .explanation {
    text-align: left;
    margin: 1.5em 0;
  }

  .back {
    margin-bottom: 2em;
  }
}

:deep(.confetti-container ) {
  position: fixed !important;
  left: 50%;
  top: 10%;
}
</style>
